/**
 * Simple Bundles IO overlay preview for Crave
 *
 * Safe approach:
 * - Does NOT modify the real gallery image
 * - Does NOT remove srcset or <source>
 * - Shows a separate overlay image only after IO selection
 * - Hides overlay when no mapped image is found
 */

(function () {
  'use strict';

  const CONFIG = {
    ioBundleContainer: '#simple-bundles-io-options',
    mainMediaSelector:
      '.product__media-item.is-active .product__media,' +
      '.product__media-item .product__media,' +
      '.product__media.media'
  };

  const imageMap = {};
  const productCache = {};

  let previewImg = null;
  let previewWrap = null;

  function normalizeOptionText(text) {
    return String(text || '')
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function resizeImage(url, width) {
    if (!url) return url;
    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      // console.warn('[SB-IMG] Failed to fetch product:', handle, e);
      return null;
    }
  }

  async function getBundledVariants() {
    if (window.__SB_BUNDLED_VARIANTS__) {
      return typeof window.__SB_BUNDLED_VARIANTS__ === 'string'
        ? JSON.parse(window.__SB_BUNDLED_VARIANTS__)
        : window.__SB_BUNDLED_VARIANTS__;
    }

    const handle = getProductHandle();
    if (!handle) return null;

    try {
      const res = await fetch(`/products/${handle}.json`);
      if (res.ok) {
        const data = await res.json();
        const product = data.product || data;

        if (product && product.metafields) {
          const sbMeta =
            product.metafields.simple_bundles ||
            product.metafields['app--4901177--simple_bundles'];

          if (sbMeta && sbMeta.bundled_variants) {
            return typeof sbMeta.bundled_variants === 'string'
              ? JSON.parse(sbMeta.bundled_variants)
              : sbMeta.bundled_variants;
          }
        }
      }
    } catch (e) {}

    const scripts = document.querySelectorAll('script[type="application/json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);

        const searchObj = (obj, depth = 0) => {
          if (depth > 6 || !obj || typeof obj !== 'object') return null;
          if (obj.bundled_variants) {
            return typeof obj.bundled_variants === 'string'
              ? JSON.parse(obj.bundled_variants)
              : obj.bundled_variants;
          }
          for (const key in obj) {
            const result = searchObj(obj[key], depth + 1);
            if (result) return result;
          }
          return null;
        };

        const found = searchObj(data);
        if (found) return found;
      } catch (e) {}
    }

    return null;
  }

  async function buildImageMap(bundledVariants) {
    if (!Array.isArray(bundledVariants) || bundledVariants.length === 0) {
      // console.warn('[SB-IMG] No bundled_variants found.');
      return;
    }

    const handleGroups = {};

    for (const bv of bundledVariants) {
      if (!bv.handle) continue;
      if (!handleGroups[bv.handle]) handleGroups[bv.handle] = [];
      handleGroups[bv.handle].push(bv);
    }

    const fetches = Object.keys(handleGroups).map(async (handle) => {
      const product = await fetchProduct(handle);
      if (!product) return;

      for (const bv of handleGroups[handle]) {
        let imageSrc = null;

        const matchedVariant = Array.isArray(product.variants)
          ? product.variants.find((v) => String(v.id) === String(bv.variant_id))
          : null;

        if (matchedVariant && matchedVariant.featured_image && matchedVariant.featured_image.src) {
          imageSrc = matchedVariant.featured_image.src;
        } else if (matchedVariant && matchedVariant.featured_image) {
          imageSrc = matchedVariant.featured_image;
        } else if (product.featured_image) {
          imageSrc = product.featured_image;
        } else if (Array.isArray(product.images) && product.images.length > 0) {
          imageSrc = product.images[0];
        }

        if (!imageSrc) continue;

        imageSrc = resizeImage(imageSrc, 1200);

        const keys = new Set();

        if (Array.isArray(bv.variant_options)) {
          for (const opt of bv.variant_options) {
            if (opt && opt.value) keys.add(normalizeOptionText(opt.value));
          }
        }

        if (bv.product_title) {
          keys.add(normalizeOptionText(bv.product_title));
          if (bv.variant_title && bv.variant_title !== 'Default Title') {
            keys.add(normalizeOptionText(`${bv.product_title} - ${bv.variant_title}`));
          }
        }

        for (const key of keys) {
          imageMap[key] = imageSrc;
        }
      }
    });

    await Promise.all(fetches);
    // console.log('[SB-IMG] Image map built:', imageMap);
  }

  function getActiveMediaContainer() {
    return (
      document.querySelector('.product__media-item.is-active .product__media') ||
      document.querySelector('.product__media-item .product__media') ||
      document.querySelector('.product__media.media')
    );
  }

  function ensurePreviewLayer() {
    const media = getActiveMediaContainer();
    if (!media) return null;

    if (!media.classList.contains('sb-io-preview-wrap')) {
      media.classList.add('sb-io-preview-wrap');
    }

    let overlay = media.querySelector('.sb-io-preview-image');

    if (!overlay) {
      overlay = document.createElement('img');
      overlay.className = 'sb-io-preview-image';
      overlay.alt = '';
      media.appendChild(overlay);
    }

    previewWrap = media;
    previewImg = overlay;
    return overlay;
  }

  function showPreview(src) {
    const overlay = ensurePreviewLayer();
    if (!overlay || !src) return;

    overlay.src = src;
    overlay.classList.add('is-visible');
  }

  function hidePreview() {
    const overlay = ensurePreviewLayer();
    if (!overlay) return;

    overlay.classList.remove('is-visible');
    overlay.removeAttribute('src');
  }

  function handleChange(select) {
    const selected = select.options[select.selectedIndex];
    if (!selected || !selected.value) {
      hidePreview();
      return;
    }

    const raw = selected.textContent || '';
    const normalized = normalizeOptionText(raw);

    // console.log('[SB-IMG] Dropdown changed:', { raw: raw.trim(), normalized });

    let imageSrc = imageMap[normalized];

    if (!imageSrc) {
      const partialMatch = Object.keys(imageMap).find(
        (key) => normalized.includes(key) || key.includes(normalized)
      );
      if (partialMatch) {
        imageSrc = imageMap[partialMatch];
        // console.log('[SB-IMG] Partial match:', partialMatch);
      }
    }

    if (imageSrc) {
      showPreview(imageSrc);
    } else {
      hidePreview();
    }
  }

  function attachListeners(container) {
    const selects = container.querySelectorAll('select');
    selects.forEach((sel) => {
      if (sel.dataset.sbImgBound) return;
      sel.dataset.sbImgBound = '1';
      sel.addEventListener('change', () => handleChange(sel));
      // console.log('[SB-IMG] Listener attached:', sel.name || sel.id || '(unnamed)');
    });
  }

  async function init() {
    ensurePreviewLayer();

    const bundledVariants = await getBundledVariants();
    if (bundledVariants) {
      // console.log('[SB-IMG] bundled_variants loaded:', bundledVariants.length);
      await buildImageMap(bundledVariants);
    } else {
      // console.warn('[SB-IMG] bundled_variants could not be loaded.');
    }

    const ioContainer = document.querySelector(CONFIG.ioBundleContainer);
    if (ioContainer) attachListeners(ioContainer);

    const observer = new MutationObserver((mutations) => {
      let shouldRefreshPreviewHost = false;

      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;

          const container =
            node.matches && node.matches(CONFIG.ioBundleContainer)
              ? node
              : node.querySelector
                ? node.querySelector(CONFIG.ioBundleContainer)
                : null;

          if (container) attachListeners(container);

          if (
            node.matches &&
            (node.matches('.product__media-item') || node.matches('.product__media'))
          ) {
            shouldRefreshPreviewHost = true;
          }
        }
      }

      if (shouldRefreshPreviewHost) {
        ensurePreviewLayer();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    console.log('[SB-IMG] Overlay preview initialized.');
  }

  document.addEventListener('click', function (e) {
    const thumbButton = e.target.closest('.thumbnail-list__item button.thumbnail');
    if (thumbButton) {
      hidePreview();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();